import { DOCTOR_PROFILE_LIMITS as L } from '@/features/doctor-profiles/types';

export interface DoctorProfileErrors {
  /** Per qualification line (index in the form). */
  lines: Record<number, string>;
  /** About the list as a whole (too many lines, API message). */
  list?: string;
}

export const hasErrors = (e: DoctorProfileErrors) =>
  Object.keys(e.lines).length > 0 || !!e.list;

/** The checks PutDoctorProfileDto makes, so most mistakes show before saving. */
export function validateProfile(lines: string[]): DoctorProfileErrors {
  const errors: DoctorProfileErrors = { lines: {} };
  const seen = new Map<string, number>();
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line.length === 0)
      errors.lines[i] = 'Enter the qualification or remove this line.';
    else if (line.length < L.minLineLength)
      errors.lines[i] = `Use at least ${L.minLineLength} characters.`;
    else if (line.length > L.maxLineLength)
      errors.lines[i] = `Use at most ${L.maxLineLength} characters.`;
    else if (seen.has(line))
      errors.lines[i] = `Same as line ${seen.get(line)! + 1}. Remove one.`;
    else seen.set(line, i);
  });
  if (lines.length > L.maxLines)
    errors.list = `Up to ${L.maxLines} lines. Remove ${lines.length - L.maxLines}.`;
  return errors;
}

/** The PUT body fields from the form: trimmed lines. */
export function toPutFields(lines: string[]) {
  return { qualifications: lines.map((l) => l.trim()) };
}

/** API 400 field errors (`qualifications`, `qualifications.3`, `photoUrl`). */
export function apiFieldErrors(
  fieldErrors: { field: string; message: string }[],
): DoctorProfileErrors {
  const errors: DoctorProfileErrors = { lines: {} };
  for (const e of fieldErrors) {
    const [field, index] = e.field.split('.');
    if (field === 'qualifications') {
      const i = Number(index);
      if (index !== undefined && Number.isInteger(i))
        errors.lines[i] ??= e.message;
      else errors.list ??= e.message;
    }
  }
  return errors;
}

/** Moves item `from` one place up (-1) or down (+1). */
export function move<T>(items: T[], from: number, by: -1 | 1): T[] {
  const to = from + by;
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
